import { multiaddr } from '@multiformats/multiaddr'
import { multiaddrToUri } from '@multiformats/multiaddr-to-uri'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { isAddress } from 'viem'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatAddress(address: string, length = 4): string {
  if (!isAddress(address)) return address
  return `${address.slice(0, 2 + length)}...${address.slice(-length)}`
}

export function validateNodeAddress(address: string): {
  isValid: boolean
  error?: string
} {
  if (!address) {
    return { isValid: false, error: 'Node address is required' }
  }

  if (address.length > 64) {
    return {
      isValid: false,
      error: `Node address must be 64 characters or less (current: ${address.length})`
    }
  }

  // Basic URL/IP validation
  const urlPattern = /^(https?:\/\/|wss?:\/\/)?[\w.-]+(:\d+)?(\/.*)?$/
  if (!urlPattern.test(address)) {
    return {
      isValid: false,
      error: 'Invalid node address format. Use URL or IP:port format.'
    }
  }

  return { isValid: true }
}

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text)
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    return new Promise((resolve, reject) => {
      if (document.execCommand('copy')) {
        resolve()
      } else {
        reject(new Error('Failed to copy to clipboard'))
      }
      document.body.removeChild(textArea)
    })
  }
}

export function getBlockExplorerUrl(chainId: number, address: string): string {
  const baseUrls: Record<number, string> = {
    1: 'https://etherscan.io',
    11155111: 'https://sepolia.etherscan.io',
  }

  const baseUrl = baseUrls[chainId]
  if (!baseUrl) return '#'

  return `${baseUrl}/tx/${address}`
}

export function formatChainName(chainId: number): string {
  const names: Record<number, string> = {
    1: 'Ethereum',
    8453: 'Base',
  }

  return names[chainId] || `Chain ${chainId}`
}

export function isValidEthereumAddress(address: string): boolean {
  return isAddress(address)
}

export function shortenHash(hash: string, length = 6): string {
  if (hash.length <= length * 2) return hash
  return `${hash.slice(0, length)}...${hash.slice(-length)}`
}

export function formatGasPrice(gasPrice: bigint): string {
  const gwei = Number(gasPrice) / 1e9
  return `${gwei.toFixed(2)} gwei`
}

export function formatEther(wei: bigint, decimals = 4): string {
  const ether = Number(wei) / 1e18
  return ether.toFixed(decimals)
}

export function debounce<TArgs extends unknown[]>(
  func: (...args: TArgs) => unknown,
  wait: number
): (...args: TArgs) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: TArgs) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function isValidMultiaddr(addrString: string) {
  try {
    multiaddr(addrString) // This throws if invalid
    return true
  } catch (err) {
    return false
  }
}
function isValidUrl(urlString: string) {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
}

export function parseMultiaddr(addrString: string) {
  if (isValidUrl(addrString)) {
    return addrString
  }
  if (isValidMultiaddr(addrString)) {
    return multiaddrToUri(addrString)
  }
  return ""
}
